import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Radio,
  Button,
  useMediaQuery,
  Divider
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { getUserLocations, setDefaultLocation } from './userLocationsApi';
import { useCurrency } from './CurrencyContext';
import CurrencySelector from './CurrencySelector';

function BlackHeader() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [locations, setLocations] = useState([]);
  const [defaultLocation, setDefaultLocationState] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [user, setUser] = useState(null);

  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const [currencySelectorOpen, setCurrencySelectorOpen] = useState(false);

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/RestaurantInfo/`)
      .then(res => res.json())
      .then(data => {
        if (data && data.PhoneNumber) {
          setPhoneNumber(data.PhoneNumber);
        }
      })
      .catch(() => {});

    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsedUser = JSON.parse(userStr);
      setUser(parsedUser);
      loadLocations(parsedUser.Id || parsedUser.id);
    }
  }, []);

  const loadLocations = async (userId) => {
    try {
      const data = await getUserLocations(userId);
      setLocations(data);
      const def = data.find(l => l.IsDefault);
      if (def) {
        setDefaultLocationState(def);
      } else if (data.length > 0) {
        setDefaultLocationState(data[0]);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleLocationChange = async (location) => {
    try {
      await setDefaultLocation(location.Id);
      setDefaultLocationState(location);

      const updatedLocations = locations.map(l => ({
        ...l,
        IsDefault: l.Id === location.Id
      }));
      setLocations(updatedLocations);

      setLocationModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Error setting default location:', error);
    }
  };

  const handleCopy = () => {
    if (phoneNumber) {
      navigator.clipboard.writeText(phoneNumber);
      setSnackbarOpen(true);
    }
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    setLanguageModalOpen(false);
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'العربية' }
  ];

  return (
    <>
      <Box
        sx={{
          width: '100%',
          bgcolor: 'common.white',
          color: 'common.black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 2 },
          py: { xs: 0.5, sm: 0.1 },
          minHeight: { xs: 32, sm: 26 },
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          fontFamily: 'Inter, Roboto, Arial, sans-serif',
          zIndex: 1200, // ✅ Lowered from 1400 so dialogs (1300+) appear above
          position: 'sticky',
          top: 0,
        }}
      >
        {/* Left Side: Language, Currency & Location */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.3, sm: 1 },
          flex: 1,
          overflow: 'hidden'
        }}>
          {/* Language Selector */}
          <IconButton
            size="small"
            onClick={() => setLanguageModalOpen(true)}
            sx={{
              color: 'common.black',
              p: { xs: 0.4, sm: 0.5 },
              minWidth: { xs: 28, sm: 32 },
              '&:hover': { color: 'primary.main' },
              '&:active': { transform: isMobile ? 'scale(0.95)' : 'none' }
            }}
          >
            <LanguageIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>

          {/* Currency Selector */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              borderLeft: '1px solid #e0e0e0',
              pl: { xs: 0.8, sm: 2 },
              pr: { xs: 0.8, sm: 2 },
              py: { xs: 0.3, sm: 0.5 },
              '&:hover': { bgcolor: 'action.hover' },
              borderRadius: 1,
              transition: 'all 0.2s',
              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
            }}
            onClick={() => setCurrencySelectorOpen(true)}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {getCurrencySymbol()} {selectedCurrency}
            </Typography>
          </Box>

          {/* Location Selector */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              borderLeft: '1px solid #e0e0e0',
              pl: { xs: 0.8, sm: 2 },
              pr: { xs: 0.5, sm: 1 },
              py: { xs: 0.3, sm: 0.5 },
              '&:hover': { bgcolor: 'action.hover' },
              borderRadius: 1,
              transition: 'all 0.2s',
              flex: 1,
              minWidth: 0,
              maxWidth: { xs: 120, sm: 180 },
              '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
            }}
            onClick={() => {
              if (user) {
                setLocationModalOpen(true);
              } else {
                navigate('/login');
              }
            }}
          >
            <LocationOnIcon
              sx={{
                color: 'primary.main',
                fontSize: { xs: 16, sm: 18 },
                mr: { xs: 0.3, sm: 0.5 },
                flexShrink: 0
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.6rem', sm: '0.75rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1
              }}
            >
              {user
                ? (defaultLocation ? defaultLocation.Name : t('locations.select', 'Select'))
                : t('locations.loginToSelect', 'Login')}
            </Typography>
          </Box>
        </Box>

        {/* Right Side: Phone Number */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.3, sm: 1 },
          flexShrink: 0
        }}>
          {phoneNumber && (
            <Typography
              variant="body2"
              onClick={handleCopy}
              sx={{
                fontWeight: 600,
                letterSpacing: 0.5,
                fontSize: { xs: 11, sm: 15 },
                fontFamily: 'inherit',
                mr: { xs: 0.3, sm: 0.5 },
                userSelect: 'all',
                color: 'common.black',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                '&:active': { transform: isMobile ? 'scale(0.98)' : 'none' }
              }}
            >
              {phoneNumber.slice(-9)}
            </Typography>
          )}
          <IconButton
            size="small"
            href={`tel:${phoneNumber}`}
            sx={{
              color: 'success.main',
              p: { xs: 0.4, sm: 0.5 },
              minWidth: { xs: 28, sm: 32 },
              '&:hover': { color: 'success.dark' },
              '&:active': { transform: 'scale(0.95)' }
            }}
          >
            <PhoneIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
          </IconButton>
        </Box>
      </Box>

      {/* ✅ Language Selection Modal - FIXED: Rendered via Portal, high z-index */}
      {languageModalOpen && (
        <Dialog
          open={languageModalOpen}
          onClose={() => setLanguageModalOpen(false)}
          maxWidth="xs"
          fullWidth
          fullScreen={false}
          disablePortal={false} // ✅ Ensures it renders in document.body portal
          slotProps={{
            backdrop: {
              sx: {
                zIndex: 99998,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }
            }
          }}
          sx={{
            zIndex: '99999 !important', // ✅ Force above everything
            position: 'fixed',
            // ✅ Ensure pointer events work
            '& .MuiDialog-container': {
              zIndex: 99999,
              pointerEvents: 'auto',
            },
            '& .MuiPaper-root': {
              pointerEvents: 'auto',
              zIndex: 99999,
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              m: { xs: 2, sm: 'auto' },
              maxWidth: { xs: '85vw', sm: 340 },
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              pointerEvents: 'auto', // ✅ Ensure clickable
            }
          }}
        >
          {/* Header with close button */}
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 3 },
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon sx={{ fontSize: { xs: 22, sm: 24 }, color: 'primary.main' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.15rem' }
                }}
              >
                {t('selectLanguage', 'Select Language')}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setLanguageModalOpen(false)}
              size="small"
              aria-label="close"
              sx={{
                color: 'grey.600',
                bgcolor: 'grey.200',
                '&:hover': {
                  bgcolor: 'grey.300',
                  color: 'grey.800'
                },
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </DialogTitle>

          {/* Language Options */}
          <DialogContent sx={{ p: 0 }}>
            <List sx={{ p: { xs: 1, sm: 1.5 } }}>
              {languages.map((lang, index) => (
                <React.Fragment key={lang.code}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleLanguageChange(lang.code)}
                      selected={i18n.language === lang.code}
                      sx={{
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 2, sm: 2.5 },
                        borderRadius: 2,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.50',
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                          '&:hover': {
                            bgcolor: 'primary.100'
                          }
                        },
                        '&:hover': {
                          bgcolor: 'grey.100'
                        },
                      }}
                    >
                      <ListItemText
                        primary={lang.label}
                        secondary={
                          i18n.language === lang.code
                            ? t('currentLanguage', 'Currently selected')
                            : null
                        }
                        primaryTypographyProps={{
                          fontWeight: i18n.language === lang.code ? 700 : 500,
                          fontSize: { xs: '1rem', sm: '1.05rem' },
                          color: i18n.language === lang.code ? 'primary.main' : 'text.primary'
                        }}
                        secondaryTypographyProps={{
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          color: 'primary.main',
                          fontWeight: 500
                        }}
                      />
                      {i18n.language === lang.code && (
                        <CheckIcon
                          sx={{
                            color: 'primary.main',
                            fontSize: { xs: 22, sm: 24 },
                            ml: 1
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                  {index < languages.length - 1 && (
                    <Divider sx={{ mx: 2, my: 0.5 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      )}

      {/* Currency Selector Dialog */}
      <CurrencySelector
        open={currencySelectorOpen}
        onClose={() => setCurrencySelectorOpen(false)}
      />

      {/* Location Selection Modal */}
      <Dialog
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '60vh' : 600,
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 }
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            {t('locations.changeLocation', 'Change Location')}
          </Typography>
          <Button
            size={isMobile ? 'small' : 'medium'}
            startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            onClick={() => {
              setLocationModalOpen(false);
              navigate('/locations/add');
            }}
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              py: { xs: 0.5, sm: 1 }
            }}
          >
            {t('locations.add', 'Add')}
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
          <List sx={{ p: 0 }}>
            {locations.map((location) => (
              <ListItem key={location.Id} disablePadding>
                <ListItemButton
                  onClick={() => handleLocationChange(location)}
                  selected={defaultLocation && defaultLocation.Id === location.Id}
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    mb: 1,
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      '&:hover': {
                        bgcolor: 'action.selected'
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 40, sm: 48 } }}>
                    <Radio
                      edge="start"
                      checked={defaultLocation && defaultLocation.Id === location.Id}
                      tabIndex={-1}
                      disableRipple
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={location.Name}
                    secondary={location.Address}
                    primaryTypographyProps={{
                      fontWeight: defaultLocation && defaultLocation.Id === location.Id ? 'bold' : 'normal',
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                    secondaryTypographyProps={{
                      fontSize: { xs: '0.75rem', sm: '0.85rem' }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {locations.length === 0 && (
              <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                <LocationOnIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                >
                  {t('locations.noLocations', 'No locations found.')}
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
      </Dialog>

      {/* Copy Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          sx={{
            width: '100%',
            fontSize: { xs: '0.85rem', sm: '0.95rem' }
          }}
        >
          {t('copiedToClipboard', 'Copied to clipboard!')}
        </Alert>
      </Snackbar>
    </>
  );
}

export default BlackHeader;